import React, { useCallback, useState, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useMedia } from "@rapid/contexts/MediaContext";
import VideoControls from "@rapid/components/file/VideoControls";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { BuildSteps } from "@rapid/types/flows";
import { useFlow } from "@rapid/contexts/FlowContext";
import { useFilePreview } from "@rapid/contexts/FilePreviewContext";
import { useModel } from "@rapid/contexts/ModelContext";
import { EmbeddedWorkflowActions } from "./EmbeddedWorkflowActions";
import { FileUploadModal } from "@rapid/components/file/UploadModal/FileUploadModal";
import { useFlowState } from "@rapid/hooks/useFlowState";
import { useCurrentMedia, useMediaStore } from "@rapid/store";
import { useCurrentWorkflow } from "@rapid/hooks/useCurrentWorkflow";
import { LoadingOverlay } from "@rapid/components/file/Preview/Loading";
import { FileInputArea } from "@rapid/components/flow/shared/FileInputArea";
import { Chip } from "@scripts/react/components/Chip";
import Button from "@scripts/react/components/Button";
import { useFileCarouselNavigation } from "@rapid/hooks/useFileCarousel";
import { useDownloadAnnotatedFile } from "@rapid/hooks/useDownloadAnnotatedFile";
import { useShareWorkflow } from "@rapid/hooks/useShareWorkflow";
import { useWorkflowObjectName } from "@rapid/hooks/useWorkflowObjectName";
import { DelayedTooltip } from "@scripts/react/components/DelayedTooltip";
import { useMediaUtils } from "@rapid/hooks/useMediaUtils";
interface EmbeddedWorkflowPreviewMobileProps {
    children: React.ReactNode;
    className?: string;
    onAddMediaItems: (files: File[], onFinally?: () => void) => Promise<void>;
}

export const EmbeddedWorkflowPreviewMobile: React.FC<EmbeddedWorkflowPreviewMobileProps> = ({
    children,
    className,
    onAddMediaItems
}) => {
    const { flowLocked } = useFlow();
    const { step } = useFlowState();
    const { hasMultipleFiles, videoControls } = useFilePreview();
    const { erroredMediaItems, orderedMediaItems } = useMedia();
    const { isDownloading } = useModel();
    const currentMedia = useCurrentMedia();
    const { currentWorkflow: workflow } = useCurrentWorkflow();
    const media = useMediaStore();
    const { setCurrentMedia } = useMediaUtils();
    // Use shared download hook
    const handleDownloadAnnotatedFileClick = useDownloadAnnotatedFile({
        watermarkUrl: "https://rapid.roboflow.com"
    });

    // Use shared share workflow hook
    const { shareUrl, copied, handleShare: handleShareClick } = useShareWorkflow();

    // Extract object names from workflow name using shared hook
    const { objectNames } = useWorkflowObjectName();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [_, setIsHovered] = useState(false);
    const [swipeDirection, setSwipeDirection] = useState<"left" | "right" | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const touchStartX = useRef<number>(0);
    const touchEndX = useRef<number>(0);
    const isReviewStep = useMemo(() => step === BuildSteps.REVIEW, [step]);

    // Use shared hooks
    const { handlePrevFile, handleNextFile } = useFileCarouselNavigation({
        mediaItems: orderedMediaItems,
        fileId: currentMedia?.file.id,
        flowLocked,
        hasMultipleFiles
    });

    const closeModal = useCallback(() => {
        setIsModalOpen(false);
    }, []);

    const addMediaItems = useCallback(
        async (files: File[]) => {
            if (flowLocked) {
                console.warn("🎠 EmbeddedWorkflowPreviewMobile: Flow is locked, returning");
                return;
            }

            return await onAddMediaItems(files, closeModal);
        },
        [onAddMediaItems, closeModal, flowLocked]
    );

    const handleFilesSelect = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const files = Array.from(e.target.files || []);
            if (files.length > 0) {
                await addMediaItems(files);
            }
            e.target.value = "";
        },
        [addMediaItems]
    );

    // Swipe gesture handlers
    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        touchStartX.current = e.touches[0].clientX;
    }, []);

    const handleTouchMove = useCallback((e: React.TouchEvent) => {
        touchEndX.current = e.touches[0].clientX;
    }, []);

    const handleTouchEnd = useCallback(() => {
        if (!hasMultipleFiles || flowLocked) return;

        const swipeDistance = touchStartX.current - touchEndX.current;
        const minSwipeDistance = 50; // Minimum distance for a swipe to be recognized

        if (Math.abs(swipeDistance) > minSwipeDistance) {
            if (swipeDistance > 0) {
                // Swiped left - go to next file
                setSwipeDirection("left");
                handleNextFile();
            } else {
                // Swiped right - go to previous file
                setSwipeDirection("right");
                handlePrevFile();
            }
        }

        // Reset values
        touchStartX.current = 0;
        touchEndX.current = 0;
    }, [hasMultipleFiles, flowLocked, handleNextFile, handlePrevFile]);

    return (
        <div
            className={twClassNames("flex h-full w-full flex-col gap-3 overflow-hidden", className)}
        >
            <div className="flex flex-shrink-0 justify-between">
                <div className="flex flex-col gap-1">
                    <span className="text-lg font-semibold text-primary">Try Out My Model</span>
                    <div className="flex min-w-0 items-center gap-1">
                        {workflow?.name && (
                            <DelayedTooltip
                                content={workflow.name}
                                className="text-base font-normal text-secondary"
                            />
                        )}
                        {objectNames && (
                            <div className="flex flex-wrap gap-1">
                                {objectNames.map((obj, index) => (
                                    <Chip key={index} color="gray" type="text" size="medium">
                                        {obj}
                                    </Chip>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <EmbeddedWorkflowActions
                    onDownloadClick={handleDownloadAnnotatedFileClick}
                    onShareClick={handleShareClick}
                    isDownloading={isDownloading}
                    shareUrl={shareUrl}
                    copied={copied}
                    isMobile={true}
                />
            </div>

            <AnimatePresence mode="wait">
                {orderedMediaItems.length === 0 ? (
                    /* Show only FileInputArea when no files are uploaded */
                    <motion.div
                        key="no-files"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="flex min-h-0 w-full flex-1 flex-col"
                    >
                        <FileInputArea
                            addMediaItems={addMediaItems}
                            erroredMediaItems={erroredMediaItems}
                            qrSize={60}
                            showAllMethods={true}
                        />
                    </motion.div>
                ) : (
                    /* Show single column layout for mobile */
                    <motion.div
                        key="with-files"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="flex min-h-0 w-full flex-1 flex-col gap-3 overflow-hidden"
                    >
                        {/* File Preview Area */}
                        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border-gray-200 bg-black shadow-lg">
                            <div
                                className="relative flex flex-1 flex-col overflow-hidden"
                                onMouseEnter={() => setIsHovered(true)}
                                onMouseLeave={() => setIsHovered(false)}
                                onTouchStart={handleTouchStart}
                                onTouchMove={handleTouchMove}
                                onTouchEnd={handleTouchEnd}
                            >
                                <AnimatePresence initial={false} custom={swipeDirection}>
                                    <motion.div
                                        key={currentMedia?.file.id}
                                        custom={swipeDirection}
                                        initial={(direction) => ({
                                            x:
                                                direction === "left"
                                                    ? "100%"
                                                    : direction === "right"
                                                      ? "-100%"
                                                      : 0,
                                            opacity: 1
                                        })}
                                        animate={{ x: 0, opacity: 1 }}
                                        exit={(direction) => ({
                                            x:
                                                direction === "left"
                                                    ? "-100%"
                                                    : direction === "right"
                                                      ? "100%"
                                                      : 0,
                                            opacity: 1
                                        })}
                                        transition={{ duration: 0.3, ease: "easeInOut" }}
                                        className="absolute inset-0 flex flex-col"
                                    >
                                        {children}
                                    </motion.div>
                                </AnimatePresence>
                                {/* File Switcher Dots - only on small breakpoints when multiple files */}
                                {hasMultipleFiles && (
                                    <div className="absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 gap-2 sm:hidden">
                                        {orderedMediaItems.map((mediaItem, index) => (
                                            <button
                                                key={mediaItem.id}
                                                onClick={() => {
                                                    // Determine direction based on index
                                                    const currentIdx = orderedMediaItems.findIndex(
                                                        (f) => f.id === currentMedia?.file.id
                                                    );
                                                    setSwipeDirection(
                                                        index > currentIdx ? "left" : "right"
                                                    );

                                                    let frame = mediaItem;

                                                    if (mediaItem.isVideo) {
                                                        const frameId =
                                                            mediaItem
                                                                .segmentToFrameMediaItemIdMap?.[0];
                                                        frame = media[frameId!];
                                                    }

                                                    setCurrentMedia({
                                                        file: mediaItem,
                                                        frameSegment: 0,
                                                        frame: frame
                                                    });
                                                }}
                                                className={twClassNames(
                                                    "h-2 w-2 rounded-full transition-all duration-200",
                                                    mediaItem.id === currentMedia?.file.id
                                                        ? "bg-white"
                                                        : "bg-white/50"
                                                )}
                                                aria-label={`Switch to file ${index + 1}`}
                                            />
                                        ))}
                                    </div>
                                )}
                                <LoadingOverlay
                                    show={isDownloading}
                                    loadingText={
                                        currentMedia?.file.isVideo
                                            ? "Processing video..."
                                            : "Preparing download..."
                                    }
                                />
                            </div>
                            {currentMedia?.file.isVideo && (
                                <VideoControls controls={videoControls} flowLocked={flowLocked} />
                            )}
                        </div>
                        {/* Upload Button */}
                        <Button
                            type="primary"
                            size="xlarge"
                            onClick={() => fileInputRef.current?.click()}
                        >
                            Add File to Test
                        </Button>{" "}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Hidden file input */}
            <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,video/*"
                onChange={handleFilesSelect}
                disabled={flowLocked}
                className="hidden"
                aria-label="Select files to upload"
            />

            {isModalOpen && (
                <FileUploadModal
                    title={isReviewStep ? "Test on More Files" : "Add Files"}
                    isModalOpen={isModalOpen}
                    setIsModalOpen={setIsModalOpen}
                    onAddMediaItems={addMediaItems}
                    erroredMediaItems={erroredMediaItems}
                />
            )}
        </div>
    );
};
